import React, { useState } from "react";
import {
  Avatar,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import DeleteIcon from "@mui/icons-material/Delete";
import VideocamIcon from "@mui/icons-material/Videocam";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LanguageIcon from "@mui/icons-material/Language";
import PreviewIcon from "@mui/icons-material/Preview";
import { AssetType, ILeanAsset } from "../../../../types/NendaTypes";
import AssetPreviewDialog, {
  isPreviewableAsset,
} from "./ContentChannels/Preview/AssetPreviewDialog";
import PlayabilityBadge from "./AssetLibrary/PlayabilityBadge";
import { CmsAssetPlayability } from "@shared/cmsAssetPlayability";

interface LeanAssetProps {
  asset: ILeanAsset;
  assetIndex: number;
  onDelete: (index: number) => void;
  dragHandleListeners: any;
  playability?: CmsAssetPlayability;
}

const styles = {
  dragHandle: {
    verticalAlign: "middle",
    display: "inline-flex",
    paddingLeft: "10px",
    borderLeft: "1px solid #cccccc",
  },
};

const LeanAsset: React.FC<LeanAssetProps> = ({
  asset,
  assetIndex,
  onDelete,
  dragHandleListeners,
  playability,
}) => {
  const theme = useTheme();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isCmsVideo = isPreviewableAsset(asset);

  const openAssetPage = () => {
    if (!isCmsVideo) return;
    window.open(
      `/admin/metadata/vod/${encodeURIComponent(asset.locator)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openPreview = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isCmsVideo) {
      setIsPreviewOpen(true);
      return;
    }
    window.open(asset.locator, "_blank", "noopener,noreferrer");
  };

  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

  return (
    /* @ts-ignore */
    <ListItem disablePadding>
      <ListItemButton
        disableRipple
        onClick={openAssetPage}
        title={isCmsVideo ? "Open asset page in a new tab" : undefined}
        sx={{ cursor: isCmsVideo ? "pointer" : "default", pr: 18 }}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              color: theme.palette.primary.light,
              bgcolor: theme.palette.primary.dark,
            }}
          >
            {asset.type == AssetType.VIDEO && (
              <Tooltip title="Video asset">
                <VideocamIcon />
              </Tooltip>
            )}
            {asset.type == AssetType.LINEAR_CHANNEL && (
              <Tooltip title="Linear channel asset">
                <VoicemailIcon />
              </Tooltip>
            )}
            {asset.type == AssetType.IMAGE && (
              <Tooltip title="Image asset">
                <PhotoCameraIcon />
              </Tooltip>
            )}
            {asset.type == AssetType.URL && (
              <Tooltip title="Web page asset">
                <LanguageIcon />
              </Tooltip>
            )}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          sx={{ minWidth: 0 }}
          primaryTypographyProps={{
            color: theme.palette.primary.dark,
            sx: { overflowWrap: "anywhere" },
          }}
          secondaryTypographyProps={{ color: theme.palette.primary.light }}
          primary={asset.name}
          secondary={
            <>
              {`Source: ${asset.source}`}{" "}
              <PlayabilityBadge playability={playability} />
            </>
          }
        />
        <ListItemSecondaryAction>
          <Tooltip title={isCmsVideo ? "Preview" : asset.locator}>
            <IconButton aria-label="preview" onClick={openPreview}>
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete from playlist">
            <IconButton
              aria-label="delete"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(assetIndex);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Drag and drop to reorder">
            <ListItemIcon
              style={styles.dragHandle}
              onClick={stopRowClick}
              {...dragHandleListeners}
            >
              <DragHandleIcon />
            </ListItemIcon>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItemButton>
      {isCmsVideo && isPreviewOpen && (
        <AssetPreviewDialog
          isOpen
          assetId={asset.locator}
          title={asset.name}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </ListItem>
  );
};

export default LeanAsset;
